import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CriticalCSS from './components/CriticalCSS';
import EnhancedCanonicalManager from './components/EnhancedCanonicalManager';
import A11yManager from './components/A11yManager';
import ContrastFixer from './components/ContrastFixer';
import RemoveNewOrderLink from './utils/RemoveNewOrderLink';
import { ThemeProvider } from './context/ThemeContext';
import { Helmet } from 'react-helmet';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/App.scss';
import './styles/AccessibilityContrast.scss';
import DebugApi from './pages/DebugApi';

// Lazy load components that aren't needed immediately
const LazyHome = lazy(() => import('./pages/Home'));
const LazyServices = lazy(() => import('./pages/Services'));
const LazyHowItWorks = lazy(() => import('./pages/HowItWorks'));
const LazyContact = lazy(() => import('./pages/Contact'));
const LazyOrder = lazy(() => import('./pages/Order'));
const LazyPrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const LazyRefundGuarantee = lazy(() => import('./pages/RefundGuarantee'));
const LazyTermsConditions = lazy(() => import('./pages/TermsConditions'));
const LazyCookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const LazyFAQ = lazy(() => import('./pages/FAQ'));
const LazyBlog = lazy(() => import('./pages/Blog'));
const LazyBlogPost = lazy(() => import('./pages/BlogPost'));
const LazyPaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const LazyPaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const LazyCollegeEssayWriting = lazy(() => import('./pages/CollegeEssayWriting'));
const LazyResearchPaperWritingServicePage = lazy(() => import('./pages/ResearchPaperWritingService'));
const LazySomeoneToTakeMyOnlineClass = lazy(() => import('./pages/SomeoneToTakeMyOnlineClass'));
const LazyProgrammingAssignmentHelpServices = lazy(() => import('./pages/ProgrammingAssignmentHelpServices'));
const LazyBestPythonAssignmentHelp = lazy(() => import('./pages/BestPythonAssignmentHelp'));
const LazyCodingAssignmentHelpServices = lazy(() => import('./pages/CodingAssignmentHelpServices'));
const LazyPowerPointPresentationService = lazy(() => import('./pages/PowerPointPresentationService'));
const LazyDissertationWritingService = lazy(() => import('./pages/DissertationWritingService'));
const LazyMathAssignmentHelpService = lazy(() => import('./pages/MathAssignmentHelpService'));
const LazyStatisticsClassHelper = lazy(() => import('./pages/StatisticsClassHelper'));
const LazyStatisticsAssignmentHelpService = lazy(() => import('./pages/StatisticsAssignmentHelpService'));
const LazyDatabaseManagementService = lazy(() => import('./pages/DatabaseManagementService'));
const LazyDatabaseAssignmentHelpService = lazy(() => import('./pages/DatabaseAssignmentHelpService'));
const LazyAffordableAccountingClassHelp = lazy(() => import('./pages/AffordableAccountingClassHelp'));
const LazyAffordableAccountingAssignmentHelp = lazy(() => import('./pages/AffordableAccountingAssignmentHelp'));
const LazyChemistryOnlineClassTaker = lazy(() => import('./pages/ChemistryOnlineClassTaker'));
const LazyPhysicsClassHelpService = lazy(() => import('./pages/PhysicsClassHelpService'));
const LazyPhysicsHomeworkHelpService = lazy(() => import('./pages/PhysicsHomeworkHelpService'));
const LazyUrgentEssayWritingHelp = lazy(() => import('./pages/UrgentEssayWritingHelp'));
const LazyHomeworkHelpService = lazy(() => import('./pages/HomeworkHelpService'));
const LazyHireSomeoneToTakeMyExam = lazy(() => import('./pages/HireSomeoneToTakeMyExam'));
const LazyOnlineClassHelpServices = lazy(() => import('./pages/OnlineClassHelpServices'));
const LazyOnlineCourseHelp = lazy(() => import('./pages/OnlineCourseHelp'));
const LazyWriteMyCaseStudy = lazy(() => import('./pages/WriteMyCaseStudy'));
const LazyLastMinuteEssayHelp = lazy(() => import('./pages/LastMinuteEssayHelp'));
const LazyAffordableDissertationWriters = lazy(() => import('./pages/AffordableDissertationWriters'));
const LazyUrgentCalculusAssignmentHelpService = lazy(() => import('./pages/UrgentCalculusAssignmentHelpService'));
const LazyUrgentChemistryAssignmentHelp = lazy(() => import('./pages/UrgentChemistryAssignmentHelp'));
const LazyUrgentAnnotatedBibliographyService = lazy(() => import('./pages/UrgentAnnotatedBibliographyService'));
const LazyHireSomeoneToTakeYourOnlineCourse = lazy(() => import('./pages/HireSomeoneToTakeYourOnlineCourse'));
const LazyHireSomeoneToDoMyCalculusClass = lazy(() => import('./pages/HireSomeoneToDoMyCalculusClass'));
const LazyPaySomeoneToTakeMyAccountingClass = lazy(() => import('./pages/PaySomeoneToTakeMyAccountingClass'));
const LazyPaySomeoneToWriteMyEssayCheap = lazy(() => import('./pages/PaySomeoneToWriteMyEssayCheap'));
const LazyHireSomeoneToTakeMyStatisticsClass = lazy(() => import('./pages/HireSomeoneToTakeMyStatisticsClass'));
const LazyAccessibilityExamples = lazy(() => import('./components/AccessibilityExamples'));
const LazyContrastDemo = lazy(() => import('./components/ContrastDemo'));

// Lazy load components that are not needed on initial render
const LazyChatWidget = lazy(() => import('./components/ChatWidget'));
const LazyCookieBanner = lazy(() => import('./components/CookieBanner'));

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div 
    className="page-loader" 
    role="status"
    aria-live="polite"
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100%'
    }}
  >
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading content, please wait...</span>
    </div>
    <p className="sr-only">Loading content, please wait...</p>
  </div>
);

// ScrollToTop component to reset scroll position when navigating
const ScrollToTop = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Global app helpers
const AppHelpers: React.FC = () => {
  return (
    <>
      <ScrollToTop />
      <RemoveNewOrderLink />
      <EnhancedCanonicalManager />
      <CriticalCSS />
      <ContrastFixer />
      <Helmet>
        {/* Explicitly tell search engines to index the site and follow links */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Enhanced resource hints for better performance */}
        <link rel="preload" href="/images/logoheader.svg" as="image" fetchPriority="high" />
        <link rel="preload" href="/fonts/main-font.woff2" as="font" type="font/woff2" crossOrigin="anonymous" />
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link rel="preconnect" href="https://fonts.googleapis.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://www.google-analytics.com" />
        {/* Performance optimization meta tags */}
        <meta httpEquiv="x-dns-prefetch-control" content="on" />
      </Helmet>
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <A11yManager>
        <AppHelpers />
        <div className="App">
            <Navigation />
            <main className="main-content" id="main-content" tabIndex={-1}>
              <Suspense fallback={<PageLoader />}>
            <Routes>
                  <Route path="/" element={<LazyHome />} />
                  <Route path="services" element={<LazyServices />} />
                  <Route path="how-it-works" element={<LazyHowItWorks />} />
                  <Route path="contact" element={<LazyContact />} />
                  <Route path="order" element={<LazyOrder />} />
                  <Route path="privacy-policy" element={<LazyPrivacyPolicy />} />
                  <Route path="terms-conditions" element={<LazyTermsConditions />} />
                  <Route path="cookie-policy" element={<LazyCookiePolicy />} />
                  <Route path="refund-guarantee" element={<LazyRefundGuarantee />} />
                  <Route path="faq" element={<LazyFAQ />} />
                  <Route path="blog" element={<LazyBlog />} />
                  <Route path="blog/:slug" element={<LazyBlogPost />} />
                  <Route path="payment-success" element={<LazyPaymentSuccess />} />
                  <Route path="payment-cancel" element={<LazyPaymentCancel />} />
                  <Route path="/college-essay-writing" element={<LazyCollegeEssayWriting />} />
                  <Route path="/research-paper-writing-service" element={<LazyResearchPaperWritingServicePage />} />
                  <Route path="someone-to-take-my-online-class" element={<LazySomeoneToTakeMyOnlineClass />} />
                  <Route path="/programming-assignment-help-service" element={<LazyProgrammingAssignmentHelpServices />} />
                  <Route path="/best-python-assignment-help" element={<LazyBestPythonAssignmentHelp />} />
                  <Route path="/coding-assignment-help-services" element={<LazyCodingAssignmentHelpServices />} />
                  <Route path="/powerpoint-presentation-service" element={<LazyPowerPointPresentationService />} />
                  <Route path="/dissertation-writing-service" element={<LazyDissertationWritingService />} />
                  <Route path="/math-assignment-help-service" element={<LazyMathAssignmentHelpService />} />
                  <Route path="/statistics-class-helper" element={<LazyStatisticsClassHelper />} />
                  <Route path="/statistics-assignment-help-service" element={<LazyStatisticsAssignmentHelpService />} />
                  <Route path="/database-management-service" element={<LazyDatabaseManagementService />} />
                  <Route path="/database-assignment-help-service" element={<LazyDatabaseAssignmentHelpService />} />
                  <Route path="/affordable-accounting-class-help" element={<LazyAffordableAccountingClassHelp />} />
                  <Route path="/affordable-accounting-assignment-help" element={<LazyAffordableAccountingAssignmentHelp />} />
                  <Route path="/chemistry-online-class-taker" element={<LazyChemistryOnlineClassTaker />} />
                  <Route path="/physics-class-help-service" element={<LazyPhysicsClassHelpService />} />
                  <Route path="/physics-homework-help-service" element={<LazyPhysicsHomeworkHelpService />} />
                  <Route path="/urgent-essay-writing-help" element={<LazyUrgentEssayWritingHelp />} />
                  <Route path="/homework-help-service" element={<LazyHomeworkHelpService />} />
                  <Route path="/hire-someone-to-take-my-exam" element={<LazyHireSomeoneToTakeMyExam />} />
                  <Route path="/online-class-help-services" element={<LazyOnlineClassHelpServices />} />
                  <Route path="/online-course-help" element={<LazyOnlineCourseHelp />} />
                  <Route path="/write-my-case-study" element={<LazyWriteMyCaseStudy />} />
                  <Route path="/last-minute-essay-help" element={<LazyLastMinuteEssayHelp />} />
                  <Route path="/affordable-dissertation-writers" element={<LazyAffordableDissertationWriters />} />
                  <Route path="/urgent-calculus-assignment-help-service" element={<LazyUrgentCalculusAssignmentHelpService />} />
                  <Route path="/urgent-chemistry-assignment-help" element={<LazyUrgentChemistryAssignmentHelp />} />
                  <Route path="/urgent-annotated-bibliography-service" element={<LazyUrgentAnnotatedBibliographyService />} />
                  <Route path="/hire-someone-to-take-your-online-course" element={<LazyHireSomeoneToTakeYourOnlineCourse />} />
                  <Route path="/hire-someone-to-do-my-calculus-class" element={<LazyHireSomeoneToDoMyCalculusClass />} />
                  <Route path="/pay-someone-to-take-my-accounting-class" element={<LazyPaySomeoneToTakeMyAccountingClass />} />
                  <Route path="/pay-someone-to-write-my-essay-cheap" element={<LazyPaySomeoneToWriteMyEssayCheap />} />
                  <Route path="/hire-someone-to-take-my-statistics-class" element={<LazyHireSomeoneToTakeMyStatisticsClass />} />
                  <Route path="/a11y" element={<LazyAccessibilityExamples />} />
                  <Route path="/contrast" element={<LazyContrastDemo />} />
                  <Route path="/debug-api" element={<DebugApi />} />
            </Routes>
              </Suspense>
          </main>
            <Footer />
            {/* Lazy load non-critical UI elements */}
            <Suspense fallback={null}>
              <LazyChatWidget />
              <LazyCookieBanner />
            </Suspense>
        </div>
        </A11yManager>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
